import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createAISocket,
  emitWithAck,
  getAuthToken,
  loadStoredConversationId,
  storeConversationId,
  waitForConnection,
} from "./aiChatClient";

const GREETING_MESSAGE = {
  id: "greeting",
  sender: "bot",
  text: "Hello! I'm Anna, your AI health assistant. I can help you understand your medical records, answer questions about medications, book appointments, and connect you with our support team. How can I help you today?",
  timestamp: new Date(),
};

let messageCounter = 0;
function nextMessageId() {
  messageCounter += 1;
  return `msg-${Date.now()}-${messageCounter}`;
}

function mapTranscriptMessage(message) {
  const role = message.role || message.sender;
  let sender = "bot";
  if (role === "user" || role === "patient") sender = "user";
  if (role === "agent") sender = "agent";
  if (role === "system") return null;

  return {
    id: message.id || nextMessageId(),
    sender,
    text: message.content || message.text || "",
    timestamp: message.createdAt ? new Date(message.createdAt) : new Date(),
    agentName: message.metadata?.agentName || undefined,
  };
}

/**
 * Manages the AI assistant conversation: socket lifecycle, streaming replies,
 * transcript persistence, and live-agent handoff.
 */
export default function useAIChat({ patientId, isOpen }) {
  const [messages, setMessages] = useState([GREETING_MESSAGE]);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [agentSession, setAgentSession] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const socketRef = useRef(null);
  const conversationIdRef = useRef(null);
  const streamingMessageIdRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const historyRequestedRef = useRef(false);

  isOpenRef.current = isOpen;

  const appendMessage = useCallback((message) => {
    setMessages((prev) => [...prev, { id: nextMessageId(), timestamp: new Date(), ...message }]);
  }, []);

  const updateMessage = useCallback((id, patch) => {
    setMessages((prev) =>
      prev.map((message) => (message.id === id ? { ...message, ...patch } : message)),
    );
  }, []);

  const removeMessage = useCallback((id) => {
    setMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const noteIncomingWhileClosed = useCallback(() => {
    if (!isOpenRef.current) {
      setUnreadCount((prev) => prev + 1);
    }
  }, []);

  const setConversationId = useCallback(
    (conversationId) => {
      conversationIdRef.current = conversationId || null;
      storeConversationId(patientId, conversationId || null);
    },
    [patientId],
  );

  const loadHistory = useCallback(
    async (socket) => {
      const storedId = loadStoredConversationId(patientId);
      if (!storedId) {
        setHistoryLoaded(true);
        return;
      }

      try {
        const result = await emitWithAck(socket, "conversation:history", {
          conversationId: storedId,
        });

        const mapped = (result.messages || [])
          .map(mapTranscriptMessage)
          .filter(Boolean);

        conversationIdRef.current = storedId;

        if (mapped.length > 0) {
          setMessages([GREETING_MESSAGE, ...mapped]);
        }

        const sessionState = result.conversation?.state?.agentSession;
        if (sessionState && sessionState.status === "active") {
          setAgentSession(sessionState);
        }
      } catch (error) {
        // Stale or inaccessible conversation — start fresh.
        setConversationId(null);
      } finally {
        setHistoryLoaded(true);
      }
    },
    [patientId, setConversationId],
  );

  // Socket lifecycle — connect once the widget is first opened.
  useEffect(() => {
    if (!isOpen || !patientId || socketRef.current || !getAuthToken()) {
      return undefined;
    }

    const socket = createAISocket({ patientId });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);

      if (!historyRequestedRef.current) {
        historyRequestedRef.current = true;
        loadHistory(socket);
      } else if (conversationIdRef.current) {
        socket.emit("conversation:join", {
          patientId: String(patientId),
          conversationId: conversationIdRef.current,
        });
      }
    });

    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => {
      setConnected(false);

      if (!historyRequestedRef.current) {
        historyRequestedRef.current = true;
        setHistoryLoaded(true);
      }
    });

    socket.on("chat:token", ({ token }) => {
      const streamingId = streamingMessageIdRef.current;
      if (!streamingId || !token) return;

      setMessages((prev) =>
        prev.map((message) =>
          message.id === streamingId
            ? { ...message, text: (message.text || "") + token }
            : message,
        ),
      );
    });

    socket.on("agent:message", (payload) => {
      const content = payload?.message?.content || payload?.message?.text;
      if (!content) return;

      appendMessage({
        sender: "agent",
        text: content,
        agentName: payload.agentName || undefined,
      });
      noteIncomingWhileClosed();
    });

    socket.on("agent:session_updated", (payload) => {
      if (!payload) return;

      if (payload.status === "resolved" || payload.status === "closed") {
        setAgentSession(null);
        appendMessage({
          sender: "bot",
          text: "Your support session has ended. I'm here if you need anything else!",
        });
      } else if (payload.status === "active" || payload.status === "queued") {
        setAgentSession((prev) => ({ ...(prev || {}), ...payload }));
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [isOpen, patientId, appendMessage, loadHistory, noteIncomingWhileClosed]);

  const applyChatResult = useCallback(
    (result, placeholderId) => {
      if (result.conversationId) {
        setConversationId(result.conversationId);
      }

      if (result.agentSession) {
        setAgentSession(
          result.agentSession.status || result.agentSession.queued
            ? result.agentSession
            : null,
        );
      }

      if (result.reply) {
        if (placeholderId) {
          updateMessage(placeholderId, { text: result.reply, streaming: false });
        } else {
          appendMessage({ sender: "bot", text: result.reply });
        }
        noteIncomingWhileClosed();
      } else if (placeholderId) {
        // Message went to a live agent; their reply arrives via agent:message.
        removeMessage(placeholderId);
      }
    },
    [appendMessage, noteIncomingWhileClosed, removeMessage, setConversationId, updateMessage],
  );

  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText || "").trim();
      if (!text || sending || !patientId) return;

      setSending(true);
      appendMessage({ sender: "user", text });

      const payload = {
        message: text,
        patientId: String(patientId),
        conversationId: conversationIdRef.current || undefined,
        appContext: { screen: window.location.pathname, app: "patient-portal" },
        metadata: { source: "patient-portal-widget" },
      };

      const socket = socketRef.current;

      let placeholderId = null;

      try {
        if (!socket) {
          throw new Error("Assistant connection is not ready yet.");
        }

        await waitForConnection(socket);

        placeholderId = nextMessageId();
        streamingMessageIdRef.current = placeholderId;
        setMessages((prev) => [
          ...prev,
          { id: placeholderId, sender: "bot", text: "", streaming: true, timestamp: new Date() },
        ]);

        const result = await emitWithAck(socket, "chat:stream", payload);
        applyChatResult(result, placeholderId);
      } catch (error) {
        if (placeholderId) {
          removeMessage(placeholderId);
        }

        appendMessage({
          sender: "bot",
          text: "Sorry, I'm having trouble reaching our servers right now. Please check your connection and try again.",
          error: true,
        });
      } finally {
        streamingMessageIdRef.current = null;
        setSending(false);
      }
    },
    [appendMessage, applyChatResult, patientId, removeMessage, sending],
  );

  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setAgentSession(null);
    setMessages([{ ...GREETING_MESSAGE, timestamp: new Date() }]);
  }, [setConversationId]);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  return useMemo(
    () => ({
      messages,
      sending,
      connected,
      agentSession,
      unreadCount,
      historyLoaded,
      sendMessage,
      startNewConversation,
      clearUnread,
    }),
    [
      messages,
      sending,
      connected,
      agentSession,
      unreadCount,
      historyLoaded,
      sendMessage,
      startNewConversation,
      clearUnread,
    ],
  );
}
